<template>
    <div class="d-flex flex-column overflow-hidden">
        <b-form
            v-on:submit.prevent="$_onSubmit"
            class="d-flex flex-column overflow-hidden"
        >
            <div class="scrollable mb-3">
                <b-container>
                    <x-select
                        v-if="$_showApiSelect"
                        id="wozApiSelect"
                        name="wozApiSelect"
                        :text="$t('woz.labelSelectApi')"
                        v-model="wozApiId"
                        :options="$_wozApiOptions"
                        @selected="$_setWozApiId($event)"
                        :simpleMode="true"
                        :required="true"
                    >
                    </x-select>
                    <category
                        :categories="categories"
                        :accordion="true"
                        :inputFields="inputFields"
                        id="wozCategories"
                        @onCategoryShow="$_onCategoryShow($event)"
                        :invalidFieldValueErrorMessage="
                            invalidFieldValueErrorMessage
                        "
                    />
                </b-container>
            </div>
            <div v-if="invalidForm" class="errormsg px-4 mb-2">
                {{ invalidFormErrorMessage }}
            </div>
            <b-container>
                <b-row>
                    <b-col class="text-right pb-4">
                        <b-button
                            variant="primary"
                            type="submit"
                            :disabled="$_submitDisabled"
                        >
                            <span>
                                {{ $t('searchButtonText') }}
                            </span>
                        </b-button>
                    </b-col>
                </b-row>
            </b-container>
        </b-form>
    </div>
</template>

<script>
    import { FIELD_TYPE, WOZ_FIELDS } from '../../../constants';
    import Category from './ApiCategory.vue';
    import XSelect from '../../../components/BaseComponents/x-select.vue';
    import {
        convertToValueTextPair,
        arraySortAscendingFunction
    } from '../../../services/utilities';

    export default {
        components: {
            category: Category,
            'x-select': XSelect
        },
        data() {
            return {
                wozApiId: null,
                invalidFieldValueErrorMessage: '',
                invalidForm: false,
                invalidFormErrorMessage: '',
                categories: [
                    {
                        label: this.$t('woz.searchCat1'),
                        category: 1,
                        expanded: true
                    },
                    {
                        label: this.$t('woz.searchCat2'),
                        category: 2,
                        expanded: false
                    },
                    {
                        label: this.$t('woz.searchCat3'),
                        category: 3,
                        expanded: false
                    }
                ],
                inputFields: [
                    {
                        label: this.$t('woz.identification'),
                        key: 'identificatie',
                        value: '',
                        category: 1,
                        maxlength: 12,
                        pattern: '^[0-9]{12}$'
                    },
                    {
                        label: this.$t('woz.postalCode'),
                        key: 'postcode',
                        value: '',
                        category: 2,
                        maxlength: 6,
                        pattern: '[1-9][0-9]{3}(?!sa|sd|ss|SA|SD|SS)[A-Za-z]{2}',
                        title: 'formaat: 1234ab of 1234AB'
                    },
                    {
                        label: this.$t('woz.houseNumber'),
                        key: 'huisnummer',
                        value: null,
                        category: 2,
                        maxlength: 1000,
                        type: FIELD_TYPE.NUMERIC
                    },
                    {
                        label: this.$t('woz.houseLetter'),
                        key: 'huisletter',
                        value: '',
                        category: 2,
                        maxlength: 1,
                        pattern: '^[a-zA-Z]{1}$',
                        title: '1 hoofdletter of 1 kleine letter'
                    },
                    {
                        label: this.$t('woz.houseNumberAddition'),
                        key: 'huisnummertoevoeging',
                        value: '',
                        category: 2,
                        maxlength: 4,
                        pattern: '^[0-9a-zA-Z]{1,4}$',
                        title: 'combinatie van hoofdletters, kleine letters en/of cijfers'
                    },
                    {
                        label: this.$t('woz.rsinNumber'),
                        key: 'rsin',
                        value: '',
                        category: 3,
                        maxlength: 9,
                        pattern: '^[0-9]{9}$',
                        title: '9 cijfers'
                    },
                    {
                        label: this.$t('woz.kvkNumber'),
                        key: 'kvkNummer',
                        value: '',
                        category: 3,
                        maxlength: 8,
                        pattern: '^[0-9]{8}$',
                        title: '8 cijfers'
                    },
                    {
                        label: this.$t('woz.addressableObjectId'),
                        key: 'adresseerbaarObjectIdentificatie',
                        value: '',
                        category: 3,
                        maxlength: 16,
                        pattern: '^[0-9]{16}$',
                        title: '16 cijfers'
                    },
                    {
                        label: this.$t('woz.numberDesignation'),
                        key: 'nummeraanduidingIdentificatie',
                        value: '',
                        category: 3,
                        maxlength: 16,
                        pattern: '^[0-9]{16}$',
                        title: '16 cijfers'
                    }
                ]
            };
        },
        computed: {
            $_categoryExpanded() {
                return this.categories.findIndex((item) => item.expanded) + 1;
            },
            $_isCategoryExpanded() {
                return (category) =>
                    category === this.$_categoryExpanded ||
                    (Array.isArray(category) &&
                        category.includes(this.$_categoryExpanded));
            },
            // List of all available WOZ apis
            $_wozApis() {
                return this.$store.getters.getUserWozApis;
            },
            // WOZ APIs as options list for x-select
            $_wozApiOptions() {
                const options = convertToValueTextPair(
                    this.$_wozApis,
                    'id',
                    'name'
                ).sort(arraySortAscendingFunction('text'));
                return options;
            },
            // Show select list if more than 1 WOZ API available
            $_showApiSelect() {
                return this.$_wozApis.length > 1;
            },
            $_submitDisabled() {
                return this.wozApiId === null;
            }
        },
        created() {
            this.$_init();
        },
        methods: {
            $_init() {
                // Set selected WOZ API ID
                // The first if there is only 1
                if (this.$_wozApis.length === 1) {
                    this.wozApiId = this.$_wozApis[0].id;
                } else {
                    // Or from saved value in state
                    this.wozApiId = this.$store.getters.getUserWozApiId;
                }
            },
            async $_onSubmit() {
                let query = {};
                // Only add fields that have a value
                this.inputFields.forEach((item) => {
                    const value =
                        typeof item.value === 'string'
                            ? item.value.trim()
                            : item.value;
                    if (
                        value !== null &&
                        value !== '' &&
                        this.$_isCategoryExpanded(item.category)
                    ) {
                        query[item.key] = value;
                        // Postal code input should be uppercase
                        if (item.key === 'postcode') {
                            query[item.key] = query[item.key].toUpperCase();
                        }
                    }
                });

                const data = {
                    id: this.wozApiId,
                    fields: WOZ_FIELDS,
                    ...query
                };

                this.$store.commit('CLEAR_SELECTIONS');
                this.$store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_START');

                if (this.$_isValidForm(query)) {
                    this.$store.dispatch('getWozData', {
                        data,
                        title: this.$t('woz.objects')
                    });
                }

                this.$store.commit('NEDGLOBE_MOBILE_DISPLAY_SELECTION_END');
            },

            $_missingSearchFieldsMessage(_query) {
                const query = JSON.parse(JSON.stringify(_query));
                if (this.$_categoryExpanded === 3) {
                    if (
                        !(query && Object.keys(query).length > 0) ||
                        (query && Object.keys(query).length > 1)
                    ) {
                        return this.$t('woz.missingSearchFieldsMax');
                    }
                } else if (!(query && Object.keys(query).length > 0)) {
                    return this.$t('woz.missingSearchFieldsMin');
                }
                const category = this.$_categoryExpanded;
                const requiredFields = {
                    1: ['identificatie'],
                    2: ['postcode', 'huisnummer'],
                    3: []
                };
                const missingFields = requiredFields[category].filter(
                    (field) => !query[field]
                );
                if (missingFields.length > 0) {
                    return this.$t(`woz.incorrectCat${category}`);
                }
                return '';
            },

            $_isValidForm(query) {
                this.invalidForm = false;
                this.invalidFormErrorMessage =
                    this.$_missingSearchFieldsMessage(query);

                if (this.invalidFormErrorMessage) {
                    this.invalidForm = true;
                    return false;
                }

                if (this.$_hasInvalidStateFields(query)) {
                    this.invalidFormErrorMessage = this.$t(
                        'incorrectInputAllFields'
                    );
                    this.invalidForm = true;
                    return false;
                }
                return true;
            },

            $_hasInvalidStateFields(query) {
                const invalidFields = this.inputFields.filter(
                    ({ state }) => state === false
                );
                return invalidFields.length > 0;
            },

            $_onCategoryShow(category) {
                // hide error message
                this.invalidForm = false;
                // empty all values
                this.inputFields.forEach((item) => {
                    if (item.value !== null && item.value !== '') {
                        if (item.type === FIELD_TYPE.NUMERIC || item.domain) {
                            item.value = null;
                        } else {
                            item.value = '';
                        }
                    }
                });
            },
            $_setWozApiId(event) {
                // Save last selected WOZ API in state
                this.$store.commit('SET_USER_WOZ_API_ID', event);
            }
        }
    };
</script>

<style scoped lang="scss">
.scrollable {
    overflow-y: auto;
}
</style>
